import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faShare,
} from "@fortawesome/free-solid-svg-icons";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";

const HourlyCommentary = () => {
  const [hourlyCommentary, setHourlyCommentary] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("authToken");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;
  const toastShownRef = useRef(false);

  const fetchHourlyCommentary = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}hourlyCommentaryReport/hourly-commentary?limit=${limit}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const data = response.data.data;
      setHourlyCommentary(data.data);
      setPageCount(data.totalPages);
      setTotal(data.total);
      if (!toastShownRef.current) {
        showToast.success("Hourly Commentary fetched successfully.");
        toastShownRef.current = true;
      }
    } catch (error) {
      console.error("Error fetching hourly report:", error);
      if (!toastShownRef.current) {
        showToast.error("Failed to fetch hourly commentary.", "error");
        toastShownRef.current = true;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHourlyCommentary();
  }, [page]);

  const handleShare = async (id) => {
    try {
      setLoading(true);

      if (!token) {
        alert("Token not found");
        return;
      }

      const response = await axios.get(
        `${API_URL}hourlyCommentaryReport/share-report/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        showToast.success(
          response.message || "Report shared successfully.",
          "success"
        );
      } else {
        showToast.error(response.message || "Failed to share report.", "error");
      }
    } catch (error) {
      console.error(
        "Error sharing report:",
        error?.response?.data || error.message
      );
      showToast.error("Failed to share the report.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const formatCommentary = (commentary) => {
    let formatted = commentary
      .replace(/\r\n|\r|\n/g, "<br>")
      .replace(/\*\*/g, "")
      .replace("Trend Overview:", "<b>Trend Overview:</b>")
      .replace("Recent Price Action:", "<b>Recent Price Action:</b>")
      .replace(
        "Volume & Momentum Analysis:",
        "<b>Volume & Momentum Analysis:</b>"
      )
      .replace(
        "Key Support & Resistance Levels:",
        "<b>Key Support & Resistance Levels:</b>"
      );

    const match = formatted.match(/NIFTY Intraday Chart Analysis \((.*?)\)/);
    if (match && match[0]) {
      formatted = formatted.replace(match[0], `<strong>${match[0]}</strong>`);
    }

    formatted +=
      "<br><br><small>The views expressed here are based on a research analyst's analysis, and there is no guaranteed outcome. This material does not contain investment advice.</small>";

    return formatted;
  };

  return (
    <div
      className="page-header-fixed page-sidebar-fixed mt-5"
      style={{ overflowX: "hidden" }}
    >
      <div className="panel panel-default with-rounded-corner with-shadow p-10">
        <div className="d-flex align-items-center justify-content-between">
          <div className="panel-heading border-0">
            <h4 className="panel-title pull-left">Hourly Commentary</h4>
          </div>
          <div className="me-3">
            <span style={{ fontSize: "0.8rem" }} className="me-2">
              {start} to {end} of {total}
            </span>
            <button
              className="btn btn-default btn-sm"
              title="Previous"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            &nbsp;
            <button
              className="btn btn-default btn-sm"
              title="Next"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === pageCount}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="table-responsive">
            <table className="table table-condensed table-bordered bg-white">
              <tbody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index}>
                    <td style={{ padding: "20px", maxWidth: "600px" }}>
                      <div className="skeleton-text" style={{ width: "60%", height: "160px", marginBottom: "15px" }}></div>
                      <div className="skeleton-text" style={{ width: "90%", marginBottom: "6px" }}></div>
                      <div className="skeleton-text" style={{ width: "85%", marginBottom: "6px" }}></div>
                      <div className="skeleton-text" style={{ width: "70%" }}></div>
                    </td>
                    <td
                      style={{
                        width: "100px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <div
                        className="skeleton-text"
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "4px",
                          margin: "0 auto",
                        }}
                      ></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : hourlyCommentary.length > 0 ? (
          <div className="table-responsive" style={{ width: "100%", overflowX: "auto" }}>
            <table className="table table-condensed table-bordered bg-white">
              <tbody>
                {hourlyCommentary.map((post) => {
                  const commentaryHTML = formatCommentary(post.chart_commentary);

                  return (
                    <tr key={post.id}>
                      <td
                        style={{
                          paddingTop: "20px",
                          paddingBottom: "20px",
                          maxWidth: "600px",
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        <a href={post.chart_url} className="html5lightbox">
                          <img
                            src={post.chart_url}
                            alt="Chart"
                            style={{ maxWidth: "400px" }}
                          />
                        </a>
                        <br />
                        <br />
                        <div
                          style={{
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                          }}
                          dangerouslySetInnerHTML={{ __html: commentaryHTML }}
                        />
                      </td>
                      <td
                        style={{
                          width: "100px",
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >
                        <button
                          className="btn btn-primary"
                          onClick={() => handleShare(post.id)}
                          title="Share Commentary"
                        >
                          <FontAwesomeIcon icon={faShare} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div>No records found</div>
        )}
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default HourlyCommentary;
